import { useState } from 'react';
import { Link } from 'react-router-dom';
import { apiClient } from '@/api/client';
import { useAuthStore } from '@/store/auth';
import {
  CheckCircleIcon,
  CheckIcon,
  BeakerIcon,
  DocumentChartBarIcon,
  ArrowLeftIcon,
} from '@heroicons/react/24/outline';
import { PolygonIcon } from '../components/icons/PolygonIcon';
import { ArgosCheckout } from '../components/ArgosCheckout';

interface SignupData {
  organization_name: string;
  admin_email: string;
  admin_name: string;
  password: string;
  confirm_password: string;
  requested_products: string[];
  seats: number;
}

const AVAILABLE_PRODUCTS = [
  { slug: 'polygon', name: 'POLYGON', description: 'Plume containment cost predictor', icon: PolygonIcon },
  { slug: 'datavault', name: 'DATA VAULT', description: 'Environmental data management platform', icon: DocumentChartBarIcon },
  { slug: 'phreatic', name: 'PHREATIC', description: 'Groundwater availability certification', icon: BeakerIcon },
];

// Shared style tokens — kept here so this page reads as a single visual unit
// matching LoginPage's aesthetic exactly (glassy inputs, gradient cyan CTA).
const INPUT_CLASS =
  'w-full px-4 py-3 bg-white/[0.04] backdrop-blur-md border border-white/10 rounded-lg text-mono-100 text-sm tracking-wide placeholder:text-mono-500/60 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.04)] focus:border-cyan-400/50 focus:bg-white/[0.06] focus:shadow-[inset_0_1px_0_0_rgba(255,255,255,0.06),0_0_0_3px_rgba(34,211,238,0.12),0_0_24px_-6px_rgba(34,211,238,0.4)] focus:outline-none transition-all duration-200';

const PRIMARY_BTN_CLASS =
  'group relative px-6 py-3 rounded-lg overflow-hidden border border-cyan-400/40 bg-gradient-to-r from-cyan-500/90 via-cyan-400/90 to-cyan-500/90 text-black text-xs font-semibold tracking-[0.35em] font-orbitron shadow-[inset_0_1px_0_0_rgba(255,255,255,0.3),0_0_28px_-6px_rgba(34,211,238,0.55)] hover:from-cyan-400 hover:via-cyan-300 hover:to-cyan-400 hover:shadow-[inset_0_1px_0_0_rgba(255,255,255,0.4),0_0_36px_-4px_rgba(34,211,238,0.75)] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200';

const SECONDARY_BTN_CLASS =
  'px-4 py-2 rounded-lg border border-white/10 bg-white/[0.04] text-mono-300 text-xs tracking-[0.3em] font-orbitron hover:border-white/20 hover:bg-white/[0.06] transition-colors';

const LABEL_CLASS = 'block text-[10px] text-mono-400 tracking-[0.3em] mb-2 font-orbitron';

export function Signup() {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [orgCode, setOrgCode] = useState('');
  const [checkoutSecret, setCheckoutSecret] = useState<string | null>(null);
  const [checkoutAmount, setCheckoutAmount] = useState<number | undefined>(undefined);
  const [checkoutLabel, setCheckoutLabel] = useState<string | undefined>(undefined);
  // Raw text for the seats field so it can be cleared/retyped freely; the
  // numeric formData.seats is clamped to >= 1 on blur.
  const [seatsInput, setSeatsInput] = useState('1');

  const [formData, setFormData] = useState<SignupData>({
    organization_name: '',
    admin_email: '',
    admin_name: '',
    password: '',
    confirm_password: '',
    requested_products: [],
    seats: 1,
  });

  const updateForm = (field: keyof SignupData, value: string | string[] | number) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError('');
  };

  const toggleProduct = (slug: string) => {
    setFormData((prev) => ({
      ...prev,
      requested_products: prev.requested_products.includes(slug)
        ? prev.requested_products.filter((p) => p !== slug)
        : [...prev.requested_products, slug],
    }));
  };

  const validateStep1 = () => {
    if (!formData.organization_name.trim()) {
      setError('ORGANIZATION NAME REQUIRED');
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!formData.admin_email.trim()) {
      setError('EMAIL REQUIRED');
      return false;
    }
    if (!formData.admin_email.includes('@')) {
      setError('INVALID EMAIL FORMAT');
      return false;
    }
    if (!formData.admin_name.trim()) {
      setError('NAME REQUIRED');
      return false;
    }
    if (formData.password.length < 8) {
      setError('PASSWORD MUST BE AT LEAST 8 CHARACTERS');
      return false;
    }
    if (formData.password !== formData.confirm_password) {
      setError('PASSWORDS DO NOT MATCH');
      return false;
    }
    return true;
  };

  const validateStep3 = () => {
    if (formData.requested_products.length === 0) {
      setError('SELECT AT LEAST ONE PRODUCT');
      return false;
    }
    return true;
  };

  const handleNext = () => {
    if (step === 1 && validateStep1()) {
      setStep(2);
    } else if (step === 2 && validateStep2()) {
      setStep(3);
    }
  };

  const handleSubmit = async () => {
    if (!validateStep3()) return;

    setLoading(true);
    setError('');

    try {
      const response = await apiClient.post('/auth/register/organization', {
        organization_name: formData.organization_name,
        // Backend still requires this field; industry is no longer collected at signup.
        industry: '',
        admin_email: formData.admin_email,
        admin_name: formData.admin_name,
        password: formData.password,
        requested_products: formData.requested_products,
        seats: formData.seats,
      });

      // Registration is a SALES INQUIRY, not an account activation. The backend
      // creates the org as PENDING; sales reviews + activates it. We do NOT log
      // the user in or grant product access here — show the inquiry-received
      // confirmation instead. The org can sign in later via /client/login once
      // sales flips it active.
      const { organization_code, checkout_secret, checkout_amount, checkout_label } = response.data;
      // Phreatic self-serve onboarding → mount Stripe Embedded Checkout in-app.
      // On payment Stripe redirects to /account, where confirm-on-return (and
      // the webhook) flip the org to the `command` tier.
      if (checkout_secret) {
        // Establish the session client-side so the post-payment return to
        // /account is authenticated (the backend also set httpOnly cookies).
        useAuthStore.getState().login({
          id: response.data.user_id,
          email: formData.admin_email,
          full_name: formData.admin_name,
          role: 'admin',
          client_id: response.data.client_id,
        } as any, '', '');
        useAuthStore.getState().setProducts([...formData.requested_products]);
        setCheckoutAmount(checkout_amount);
        setCheckoutLabel(checkout_label);
        setCheckoutSecret(checkout_secret);
        setLoading(false);
        return;
      }
      setOrgCode(organization_code || '');
      setSubmitted(true);
      setLoading(false);
    } catch (err: any) {
      console.error('Registration error:', err.response?.data);
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        const firstError = detail[0];
        const field = firstError?.loc?.join('.') || 'unknown';
        const msg = firstError?.msg || 'Validation failed';
        setError(`${field.toUpperCase()}: ${msg}`);
      } else {
        setError(typeof detail === 'string' ? detail : 'REGISTRATION FAILED');
      }
      setLoading(false);
    }
  };

  // Background decoration shared by main + success layouts. Zoomed-in ARGOS
  // logo (left side only) — matches the LoginPage + landing page sections.
  const PageBackground = () => (
    <>
      <img
        src="/argos-full-black.png"
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute top-[58%] left-[-5vw] -translate-y-1/2 h-[250vw] md:h-[400vh] w-auto max-w-none object-contain opacity-30 select-none [filter:drop-shadow(0_18px_40px_rgba(0,0,0,0.55))]"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_60%_45%_at_50%_25%,_rgba(255,255,255,0.10)_0%,_rgba(255,255,255,0.03)_40%,_transparent_70%)]"
      />
    </>
  );

  // Back-to-home link, top-left on every signup layout.
  const BackHome = () => (
    <div className="absolute top-0 left-0 right-0 z-20 pointer-events-none">
      <div className="max-w-7xl mx-auto px-6 pt-4">
        <Link
          to="/"
          className={`pointer-events-auto inline-flex items-center gap-1.5 ${SECONDARY_BTN_CLASS}`}
        >
          <ArrowLeftIcon className="w-4 h-4" />
          HOME
        </Link>
      </div>
    </div>
  );

  // Success screen after submission
  if (submitted) {
    return (
      <div className="min-h-screen bg-mono-700 flex items-center justify-center py-12 px-4 relative overflow-hidden">
        <PageBackground />
        <BackHome />

        <div className="relative z-10 max-w-lg w-full">
          <div className="text-center">
            <div className="inline-flex items-center justify-center p-4 border border-cyan-400/30 bg-cyan-500/10 rounded-full mb-6 shadow-[0_0_28px_-6px_rgba(34,211,238,0.55)]">
              <CheckCircleIcon className="h-12 w-12 text-cyan-400" />
            </div>
            <h2 className="text-xl font-bold text-mono-100 tracking-[0.3em] mb-4 font-orbitron">
              INQUIRY RECEIVED
            </h2>
            <p className="text-sm text-mono-400 mb-8">
              Our sales team will contact you shortly to finalize your order and enable your selected products.
            </p>

            <div className="border border-white/10 bg-white/[0.04] backdrop-blur-md rounded-lg p-4 mb-6">
              <div className="flex items-center justify-center gap-3">
                <div className="flex items-center gap-2">
                  <div className="h-2 w-2 rounded-full bg-amber-500 animate-pulse" />
                  <span className="text-xs text-amber-400 tracking-widest font-orbitron">NOT ACTIVE</span>
                </div>
                <span className="text-mono-500">|</span>
                <span className="text-xs text-mono-400 font-mono">{orgCode}</span>
              </div>
            </div>

            <div className="border border-cyan-400/30 bg-cyan-500/10 backdrop-blur-md rounded-lg p-6 mb-8 shadow-[0_0_28px_-12px_rgba(34,211,238,0.4)]">
              <p className="text-[10px] text-cyan-400 tracking-[0.3em] mb-2 font-orbitron">YOUR ORGANIZATION CODE</p>
              <p className="text-2xl font-mono font-bold text-mono-100 tracking-wider">{orgCode}</p>
              <p className="text-[10px] text-mono-500 mt-2">
                Share this code with employees to join your organization
              </p>
            </div>

            <div className="space-y-3">
              <Link
                to="/client/login"
                className="group relative block w-full py-3.5 rounded-lg overflow-hidden border border-cyan-400/40 bg-gradient-to-r from-cyan-500/90 via-cyan-400/90 to-cyan-500/90 text-black text-xs font-semibold tracking-[0.35em] font-orbitron shadow-[inset_0_1px_0_0_rgba(255,255,255,0.3),0_0_28px_-6px_rgba(34,211,238,0.55)] hover:from-cyan-400 hover:via-cyan-300 hover:to-cyan-400 hover:shadow-[inset_0_1px_0_0_rgba(255,255,255,0.4),0_0_36px_-4px_rgba(34,211,238,0.75)] transition-all duration-200"
              >
                <span className="relative z-10">ACCESS CLIENT DASHBOARD</span>
                <span className="absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-white/15 to-transparent pointer-events-none" />
              </Link>
              <Link
                to="/"
                className="block w-full py-3 rounded-lg border border-white/10 bg-white/[0.04] text-mono-300 text-xs tracking-[0.3em] font-orbitron hover:border-white/20 hover:bg-white/[0.06] transition-colors"
              >
                BROWSE PRODUCTS
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-mono-700 flex items-center justify-center py-12 px-4 relative overflow-hidden">
      <PageBackground />
      <BackHome />

      {checkoutSecret && (
        <ArgosCheckout
          clientSecret={checkoutSecret}
          returnUrl={`${window.location.origin}/account?onboarded=1`}
          onClose={() => setCheckoutSecret(null)}
          title="Phreatic Onboarding"
          label={checkoutLabel}
          amount={checkoutAmount}
        />
      )}

      <div className="relative z-10 max-w-4xl w-full">
        {/* Page subtitle */}
        <div className="text-center mb-8">
          <h2 className="text-sm font-bold text-mono-100 tracking-[0.3em] mb-2 font-orbitron">
            REGISTER ORGANIZATION
          </h2>
          <p className="text-[10px] text-mono-500 tracking-wider font-orbitron">
            SET UP YOUR ORGANIZATION ACCOUNT
          </p>
        </div>

        {/* Progress indicator */}
        <div className="flex items-center justify-center mb-12">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center">
              <div
                className={`w-9 h-9 flex items-center justify-center border rounded-lg text-xs tracking-widest font-orbitron transition-all ${
                  s === step
                    ? 'border-cyan-400/50 bg-gradient-to-br from-cyan-500/20 to-cyan-400/10 text-cyan-300 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.06),0_0_24px_-6px_rgba(34,211,238,0.4)]'
                    : s < step
                    ? 'border-cyan-500/30 bg-cyan-500/10 text-cyan-400'
                    : 'border-white/10 bg-white/[0.02] text-mono-500/60'
                }`}
              >
                {s}
              </div>
              {s < 3 && (
                <div className={`w-16 h-px transition-colors ${s < step ? 'bg-cyan-500/40' : 'bg-mono-800'}`} />
              )}
            </div>
          ))}
        </div>

        {/* Step 1: Organization */}
        {step === 1 && (
          <div className="max-w-md mx-auto">
            <div className="space-y-6">
              <div>
                <label className={LABEL_CLASS}>ORGANIZATION NAME</label>
                <input
                  type="text"
                  value={formData.organization_name}
                  onChange={(e) => updateForm('organization_name', e.target.value)}
                  className={INPUT_CLASS}
                  placeholder="ACME CORPORATION"
                  autoFocus
                />
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Admin Account */}
        {step === 2 && (
          <div className="max-w-md mx-auto">
            <div className="space-y-6">
              <div>
                <label className={LABEL_CLASS}>FULL NAME</label>
                <input
                  type="text"
                  value={formData.admin_name}
                  onChange={(e) => updateForm('admin_name', e.target.value)}
                  className={INPUT_CLASS}
                  autoFocus
                />
              </div>

              <div>
                <label className={LABEL_CLASS}>EMAIL</label>
                <input
                  type="email"
                  value={formData.admin_email}
                  onChange={(e) => updateForm('admin_email', e.target.value)}
                  className={INPUT_CLASS}
                  placeholder="you@company.com"
                  autoComplete="email"
                />
              </div>

              <div>
                <label className={LABEL_CLASS}>PASSWORD</label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => updateForm('password', e.target.value)}
                  className={INPUT_CLASS}
                  autoComplete="new-password"
                />
              </div>

              <div>
                <label className={LABEL_CLASS}>CONFIRM PASSWORD</label>
                <input
                  type="password"
                  value={formData.confirm_password}
                  onChange={(e) => updateForm('confirm_password', e.target.value)}
                  className={INPUT_CLASS}
                  autoComplete="new-password"
                />
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Products of Interest */}
        {step === 3 && (
          <div>
            <div className="text-center mb-8">
              <h3 className="text-lg font-bold text-mono-100 tracking-[0.3em] mb-2 font-orbitron">
                PRODUCTS OF INTEREST
              </h3>
              <p className="text-xs text-mono-500 tracking-wider font-orbitron">
                SELECT THE PRODUCTS YOU'RE INTERESTED IN
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
              {AVAILABLE_PRODUCTS.map((product) => {
                const IconComponent = product.icon;
                const isSelected = formData.requested_products.includes(product.slug);

                return (
                  <button
                    key={product.slug}
                    type="button"
                    onClick={() => toggleProduct(product.slug)}
                    className={`relative rounded-lg border p-5 text-left transition-all backdrop-blur-md ${
                      isSelected
                        ? 'border-cyan-400/50 bg-cyan-500/15 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.06),0_0_24px_-6px_rgba(34,211,238,0.4)]'
                        : 'border-white/10 bg-white/[0.04] hover:border-cyan-400/30 hover:bg-cyan-500/5'
                    }`}
                  >
                    <div
                      className={`absolute top-3 right-3 w-5 h-5 border rounded flex items-center justify-center ${
                        isSelected ? 'border-cyan-400 bg-cyan-400' : 'border-white/20'
                      }`}
                    >
                      {isSelected && <CheckIcon className="w-3 h-3 text-black" />}
                    </div>

                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 rounded-md bg-cyan-500/10 border border-cyan-500/30">
                        <IconComponent className="w-5 h-5 text-cyan-400" />
                      </div>
                      <span className="text-mono-100 font-medium tracking-wider font-orbitron">
                        {product.name}
                      </span>
                    </div>
                    <p className="text-[11px] text-mono-400 leading-relaxed">
                      {product.description}
                    </p>
                  </button>
                );
              })}
            </div>

            <div className="text-center text-[10px] text-mono-500 tracking-wider font-orbitron">
              SELECTED: {formData.requested_products.length} PRODUCT{formData.requested_products.length !== 1 ? 'S' : ''}
            </div>

            {formData.requested_products.includes('phreatic') && (
              <div className="max-w-md mx-auto mt-4">
                <label className={LABEL_CLASS}>NUMBER OF USERS (SEATS)</label>
                <input
                  type="number"
                  min={1}
                  value={seatsInput}
                  onChange={(e) => {
                    const raw = e.target.value;
                    setSeatsInput(raw);
                    const n = parseInt(raw, 10);
                    if (!isNaN(n) && n >= 1) updateForm('seats', n);
                  }}
                  onBlur={() => {
                    const n = parseInt(seatsInput, 10);
                    const valid = !isNaN(n) && n >= 1 ? n : 1;
                    updateForm('seats', valid);
                    setSeatsInput(String(valid));
                  }}
                  className={INPUT_CLASS}
                />
                <p className="text-[10px] text-mono-500 mt-1 tracking-wider">
                  Onboarding &amp; dashboard pricing scales by seat band (1–5 / 6–20 / 21–50 / 51+).
                </p>
              </div>
            )}
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="max-w-md mx-auto mt-6 border border-red-500/30 bg-red-500/10 backdrop-blur-md p-3 text-center rounded-lg">
            <p className="text-xs text-red-400 tracking-wider">{error}</p>
          </div>
        )}

        {/* Navigation */}
        <div className="max-w-md mx-auto flex items-center justify-between mt-8">
          {step > 1 ? (
            <button onClick={() => setStep(step - 1)} className={SECONDARY_BTN_CLASS}>
              BACK
            </button>
          ) : (
            <Link to="/client" className={SECONDARY_BTN_CLASS}>
              CANCEL
            </Link>
          )}

          {step < 3 ? (
            <button onClick={handleNext} className={PRIMARY_BTN_CLASS}>
              <span className="relative z-10">CONTINUE</span>
              <span className="absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-white/15 to-transparent pointer-events-none" />
            </button>
          ) : (
            <button onClick={handleSubmit} disabled={loading} className={PRIMARY_BTN_CLASS}>
              <span className="relative z-10">
                {loading ? 'SUBMITTING...' : (formData.requested_products.includes('phreatic') ? 'CONTINUE TO PAYMENT' : 'SUBMIT REQUEST')}
              </span>
              <span className="absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-white/15 to-transparent pointer-events-none" />
            </button>
          )}
        </div>

        {/* Footer link */}
        <div className="mt-8 text-center">
          <p className="text-[10px] text-mono-500 tracking-wider">
            ALREADY HAVE AN ACCOUNT?{' '}
            <Link to="/client/login" className="text-cyan-400 hover:text-cyan-300">
              LOGIN
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
